import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { DiffView } from "./TaskNarrative";
import {
  FileDiff, Check, Terminal, History, GitCommit, Files, FileCode, GitBranch, GitMerge,
  Loader2, Repeat, X as XIcon, Search as SearchIcon, Clock, Sparkles, AlertTriangle, Plug,
  RotateCcw, Download,
} from "lucide-react";

function Empty({ icon: Icon, label }) {
  return <div className="text-center py-12 text-gray-400"><Icon className="w-6 h-6 mx-auto mb-2 opacity-50" /><div className="text-xs">{label}</div></div>;
}

export function DiffPanel({ diffs }) {
  if (diffs.length === 0) return <Empty icon={FileDiff} label="Proposed diffs appear here" />;
  return (
    <div className="space-y-3">
      {diffs.map((d, i) => (
        <div key={i} className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="px-3 py-1.5 bg-gray-50 text-[12px] font-mono flex items-center gap-1.5 border-b">
            {d.kind === "applied" ? <Check className="w-3 h-3 text-emerald-600" /> : <FileDiff className="w-3 h-3 text-gray-400" />}{d.file}
          </div>
          {d.diff && <DiffView diff={d.diff} />}
        </div>
      ))}
    </div>
  );
}

export function LogsPanel({ logs }) {
  if (logs.length === 0) return <Empty icon={Terminal} label="Command output appears here" />;
  return (
    <pre className="bg-[#0A0A0A] text-gray-200 rounded-lg p-3 text-[12px] font-mono whitespace-pre-wrap cadence-scroll">
      {logs.map((l, i) => <span key={i} className={l.stream === "stderr" ? "text-red-400" : l.stream === "exit" ? "text-blue-400" : ""}>{l.text}</span>)}
    </pre>
  );
}

// Live checkpoint events carry the commit hash directly; older/persisted rows only
// embed it in the label ("Committed 2 file(s) [abc1234]") — parse defensively.
function checkpointCommit(c) {
  if (c?.commit && /^[0-9a-f]{4,40}$/i.test(c.commit)) return c.commit;
  const m = /\[([0-9a-f]{4,40})\]\s*$/i.exec(c?.label || "");
  return m ? m[1] : null;
}

export function CheckpointsPanel({ checkpoints, onRestore }) {
  if (checkpoints.length === 0) return <Empty icon={History} label="Checkpoints are created when edits are merged" />;
  return (
    <div className="space-y-2">
      {checkpoints.map((c, i) => {
        const commit = checkpointCommit(c);
        return (
          <div key={i} className="border border-gray-200 rounded-lg px-3 py-2 flex items-center gap-2">
            <GitCommit className="w-4 h-4 text-gray-400 shrink-0" />
            <div className="min-w-0 flex-1"><div className="text-[13px] text-gray-800">{c.label}</div><div className="text-[11px] text-gray-400 font-mono">{(c.files || []).join(", ")}</div></div>
            {commit && onRestore && (
              <button data-testid="checkpoint-restore" onClick={() => onRestore(c, commit)} title={`Reset the branch to ${commit}`}
                className="shrink-0 text-[11px] text-gray-500 border border-gray-200 rounded-md px-2 py-1 hover:bg-gray-50 hover:text-gray-900 hover:border-gray-300 transition-colors inline-flex items-center gap-1">
                <RotateCcw className="w-3 h-3" /> Restore
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

const SEV = {
  critical: "bg-red-50 text-red-700",
  high: "bg-red-50 text-red-700",
  medium: "bg-amber-50 text-amber-700",
  low: "bg-gray-100 text-gray-500",
};

export function IssuesPanel({ issues }) {
  // open issues only — friction + verification findings folded into the session handoff
  const open = (issues || []).filter((i) => i && i.status !== "resolved");
  if (!open.length) return <Empty icon={AlertTriangle} label="No pending issues" />;
  return (
    <div className="space-y-2">
      <p className="label-caps text-gray-400 px-0.5">Pending issues &amp; observed friction</p>
      {open.map((it, i) => (
        <div key={i} className="border border-gray-200 rounded-lg px-3 py-2 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" />
          <div className="min-w-0 flex-1">
            <div className="text-[13px] text-gray-800 break-words">{it.title}</div>
            <span className={`mt-1 inline-flex text-[11px] font-600 px-2 py-0.5 rounded-md ${SEV[it.severity] || SEV.medium}`}>
              {it.severity || "medium"}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

export function FilesPanel({ tree, openFile, onOpen }) {
  if (!tree.length) return <Empty icon={Files} label="Pair a host to browse local files" />;
  return (
    <div>
      <div className="space-y-0.5 mb-3">
        {tree.filter((t) => t.type === "file").slice(0, 100).map((t) => (
          <button key={t.path} data-testid="file-tree-item" onClick={() => onOpen(t.path)}
            className="w-full text-left px-2 py-1 rounded hover:bg-gray-100 text-[12px] font-mono text-gray-600 flex items-center gap-1.5">
            <FileCode className="w-3.5 h-3.5 text-gray-400" /> {t.path}
          </button>
        ))}
      </div>
      {openFile && (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="px-3 py-1.5 bg-gray-50 text-[12px] font-mono border-b">{openFile.path}</div>
          <pre className="p-3 text-[12px] font-mono overflow-x-auto cadence-scroll max-h-80 text-gray-700">{openFile.content}</pre>
        </div>
      )}
    </div>
  );
}

export function GitPanel({ status, host, githubLinked, onMerge, onRefresh, onSync }) {
  if (!host) return <Empty icon={GitCommit} label="Pair a host to see git status" />;
  if (!status) return <div className="py-12 grid place-items-center text-gray-400"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  return (
    <div className="space-y-3">
      <div className="border border-gray-200 rounded-lg p-3">
        <div className="label-caps mb-1">Branch</div>
        <div className="text-sm flex items-center gap-1.5"><GitBranch className="w-4 h-4 text-gray-400" /> {status.branch}
          {status.is_main && <span className="text-[9px] text-gray-400 uppercase ml-1">main</span>}
          {(status.remote_ahead > 0 || status.remote_behind > 0) && (
            <span data-testid="git-sync-chip" title="Commits ahead / behind GitHub"
              className="ml-auto text-[10px] font-mono px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
              ↑{status.remote_ahead} ↓{status.remote_behind}
            </span>
          )}</div>
      </div>
      <div className="border border-gray-200 rounded-lg p-3">
        <div className="label-caps mb-1">Last commit</div>
        <div className="text-[13px] font-mono text-gray-600 break-all">{status.last_commit || "—"}</div>
      </div>
      {!status.is_main && (
        <div className="border border-gray-200 rounded-lg p-3">
          <div className="label-caps mb-1">Vs main</div>
          <div className="text-sm text-gray-700">{status.ahead} ahead · {status.behind} behind</div>
          <button data-testid="git-merge" onClick={onMerge} disabled={status.ahead === 0}
            className="mt-2 w-full bg-gray-900 text-white rounded-lg px-3 py-1.5 text-sm font-medium hover:bg-black disabled:opacity-40 inline-flex items-center justify-center gap-1.5">
            <GitMerge className="w-4 h-4" /> Merge to main
          </button>
        </div>
      )}
      <div className="border border-gray-200 rounded-lg p-3">
        <div className="label-caps mb-1">Working tree</div>
        {status.dirty
          ? <div className="text-sm text-amber-700">{status.changed_files.length} uncommitted change(s)</div>
          : <div className="text-sm text-emerald-700 flex items-center gap-1.5"><Check className="w-4 h-4" /> Clean</div>}
        {status.changed_files?.length > 0 && (
          <div className="mt-2 space-y-0.5">
            {status.changed_files.map((f, i) => <div key={i} className="text-[12px] font-mono text-gray-500">{f}</div>)}
          </div>
        )}
      </div>
      <div className="flex items-center gap-4">
        {githubLinked && (
          <button data-testid="git-sync" onClick={onSync}
            className="text-xs text-gray-500 hover:text-gray-900 inline-flex items-center gap-1">
            <Download className="w-3 h-3" /> Sync from GitHub
          </button>
        )}
        <button data-testid="git-refresh" onClick={onRefresh} className="text-xs text-gray-500 hover:text-gray-900 inline-flex items-center gap-1"><Repeat className="w-3 h-3" /> Refresh</button>
      </div>
    </div>
  );
}

export function EmptyTask({ host, project, version, onSuggest }) {
  const navigate = useNavigate();
  return (
    <div className="text-center py-16 animate-fade-up">
      <div className="w-12 h-12 rounded-xl bg-gray-100 grid place-items-center mx-auto mb-4"><Sparkles className="w-6 h-6 text-gray-400" /></div>
      <h3 className="font-heading font-700 text-lg text-gray-900">Start a task</h3>
      <p className="text-sm text-gray-500 mt-1 max-w-sm mx-auto">
        {host ? <>Cadence works on <span className="font-mono">{project?.name}</span> ({version?.branch || "main"}) via <span className="font-600">{host.name}</span>, proposes changes, and waits for your approval.</>
              : "Pair a host in Integrations to give Cadence access to your local files."}
      </p>
      {!host && (
        <button data-testid="empty-pair-host" onClick={() => navigate("/integrations")}
          className="mt-5 bg-black text-white rounded-lg px-4 py-2.5 text-sm font-medium hover:bg-gray-900 inline-flex items-center gap-2">
          <Plug className="w-4 h-4" /> Pair a host
        </button>
      )}
      <div className="flex flex-wrap gap-2 justify-center mt-5">
        {["Explain this project's structure", "Add a dark mode toggle", "Refactor app.js for readability"].map((s) => (
          <button key={s} type="button" data-testid="empty-suggestion" onClick={() => onSuggest && onSuggest(s)}
            className="text-xs text-gray-500 border border-gray-200 rounded-full px-3 py-1.5 bg-white hover:bg-gray-50 hover:text-gray-900 hover:border-gray-300 transition-colors">{s}</button>
        ))}
      </div>
    </div>
  );
}

export function HistoryModal({ projectId, onClose, onOpen }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [retention, setRetention] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const t = setTimeout(async () => {
      setLoading(true);
      const { data } = await api.get(`/projects/${projectId}/history`, { params: { q } });
      setItems(data.sessions); setRetention(data.retention_days); setLoading(false);
    }, 200);
    return () => clearTimeout(t);
  }, [q, projectId]);
  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={onClose}>
      <div data-testid="history-modal" className="bg-white rounded-2xl w-full max-w-lg p-5 shadow-xl animate-fade-up" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading font-700 text-lg flex items-center gap-2"><History className="w-4.5 h-4.5" /> Session history</h3>
          <button data-testid="modal-close" onClick={onClose} className="text-gray-400 hover:text-gray-900 p-1"><XIcon className="w-4 h-4" /></button>
        </div>
        <div className="relative mb-3">
          <SearchIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input data-testid="history-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search sessions"
            className="pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg w-full outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        <div className="max-h-80 overflow-y-auto cadence-scroll space-y-1">
          {loading ? <div className="py-8 grid place-items-center text-gray-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
            : items.length === 0 ? <div className="py-8 text-center text-sm text-gray-400">No sessions found.</div>
            : items.map((s) => (
              <button key={s.id} data-testid="history-item" onClick={() => onOpen(s)}
                className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-50 flex items-center justify-between">
                <span className="text-sm text-gray-800 truncate flex items-center gap-2"><Clock className="w-3.5 h-3.5 text-gray-300" /> {s.title}</span>
                <span className="text-[11px] text-gray-400">{(s.updated_at || "").slice(0, 10)}</span>
              </button>
            ))}
        </div>
        {retention && <p className="text-[11px] text-gray-400 mt-3">Solo plan keeps {retention} days of history. Upgrade to Pro for unlimited.</p>}
      </div>
    </div>
  );
}
